import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ArrowLeft, Send, Sparkles, Zap } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import './CreateWithAIPage.css';

const MAX_CHARS = 3000;

const EXAMPLE_PROMPTS_ES = [
  'Push Pull Legs 6 días, top set + back off sets, hipertrofia avanzada',
  'Torso-pierna 4 días para principiante, 3x10-12 por ejercicio',
  'Full body 3 días enfocado en fuerza: sentadilla, peso muerto y press banca',
  'PPL 5 días con frecuencia 2 por músculo, volumen moderado con técnicas intensas',
];

const EXAMPLE_PROMPTS_EN = [
  'Push Pull Legs 6 days, top set + back off sets, advanced hypertrophy',
  'Upper-lower 4 days for beginners, 3x10-12 per exercise',
  'Full body 3 days strength focused: squat, deadlift and bench press',
  'PPL 5 days with 2x frequency per muscle, moderate volume with intensity techniques',
];

const CreateWithAIPage = () => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const examples = i18n.language.startsWith('es') ? EXAMPLE_PROMPTS_ES : EXAMPLE_PROMPTS_EN;

  const handleGenerate = async () => {
    if (!input.trim() || loading) return;
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch('/api/ai/generate-workout', {
        method: 'POST',
        body: JSON.stringify({ input }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('create_ai.error_generic'));
      navigate('/workout/preview', { state: { workout: data } });
    } catch (err) {
      setError(err.message || t('create_ai.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  const charsLeft = MAX_CHARS - input.length;
  const isNearLimit = charsLeft < MAX_CHARS * 0.1;

  return (
    <div className="page-container animate-fade-in">
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="btn btn-icon btn-secondary"
            onClick={() => navigate(-1)}
            style={{ flexShrink: 0 }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 className="header-title">{t('create_ai.title')}</h1>
              <span className="ai-badge">
                <Sparkles size={10} />
                IA
              </span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '2px' }}>
              {t('create_ai.subtitle')}
            </p>
          </div>
        </div>
        <img src="/dragon-logo.png" alt="DragonFit" className="header-logo" />
      </div>

      {/* Input card */}
      <div className="card mb-4" style={{ padding: '16px' }}>
        <label className="form-label" style={{
          display: 'block', fontSize: '11px', fontWeight: '700',
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--muted-foreground)', marginBottom: '10px'
        }}>
          {t('create_ai.description')}
        </label>
        <textarea
          style={{
            width: '100%', minHeight: '130px', resize: 'none',
            background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--foreground)', fontSize: '15px', lineHeight: '1.65',
            fontFamily: 'inherit', padding: 0,
          }}
          placeholder={t('create_ai.placeholder')}
          value={input}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) setInput(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(); }
          }}
          disabled={loading}
        />
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: '12px', paddingTop: '12px',
          borderTop: '1px solid var(--border)',
        }}>
          <span style={{
            fontSize: '12px',
            color: isNearLimit ? 'var(--destructive)' : 'var(--muted-foreground)',
            transition: 'color 0.2s ease',
          }}>
            {input.length}/{MAX_CHARS}
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleGenerate}
            disabled={loading || !input.trim()}
            style={{ gap: '6px' }}
          >
            <Send size={14} />
            {t('create_ai.generate_btn')}
          </button>
        </div>
      </div>

      {error && (
        <div className="card mb-4" style={{
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.3)',
          padding: '14px 16px',
        }}>
          <p style={{ color: 'var(--destructive)', fontSize: '14px', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Examples */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Zap size={14} style={{ color: 'var(--primary)' }} />
          <p style={{
            fontSize: '11px', fontWeight: '700',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            color: 'var(--muted-foreground)', margin: 0,
          }}>
            {t('create_ai.examples_title')}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {examples.map((example, i) => (
            <button
              key={i}
              onClick={() => setInput(example)}
              className="ai-example-btn"
              style={{
                borderColor: input === example ? 'rgba(var(--primary-rgb),0.45)' : 'var(--border)',
                background: input === example ? 'rgba(var(--primary-rgb),0.1)' : 'rgba(255,255,255,0.02)',
                color: input === example ? 'var(--primary)' : 'var(--foreground)',
              }}
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      {loading && createPortal(
        <div className="ai-loading-overlay">
          <div className="dragon-loader">🐉</div>
          <p style={{ color: 'white', fontSize: '17px', fontWeight: '700', margin: '8px 0 4px' }}>
            {t('create_ai.generating')}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', margin: 0 }}>
            {t('create_ai.generating_sub')}
          </p>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CreateWithAIPage;
